import { bufferSource, concat } from './encoding.ts'

// "Sealed box": ephemeral X25519 key → ECDH with the recipient → HKDF-SHA256
// → AES-256-GCM. Anyone holding the recipient's PUBLIC key can seal (a human
// writing a value in the browser, an approver delivering a KEK); only the
// PRIVATE key opens (a daemon, in memory).
const subtle = globalThis.crypto.subtle
const PUB_BYTES = 32
const NONCE_BYTES = 12
const TAG_BYTES = 16
const X25519 = { name: 'X25519' } as const
const INFO = new TextEncoder().encode('stoaria-vault sealed-box v1')

export async function generateSealingKeyPair(): Promise<{
  publicKey: Uint8Array
  privateKey: Uint8Array
}> {
  const pair = await subtle.generateKey(X25519, true, ['deriveBits'])
  return {
    publicKey: new Uint8Array(await subtle.exportKey('raw', pair.publicKey)),
    privateKey: new Uint8Array(
      await subtle.exportKey('pkcs8', pair.privateKey),
    ),
  }
}

async function deriveAesKey(
  privateKey: CryptoKey,
  peerPublicRaw: Uint8Array,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const peer = await subtle.importKey(
    'raw',
    bufferSource(peerPublicRaw),
    X25519,
    false,
    [],
  )
  const shared = await subtle.deriveBits(
    { name: 'X25519', public: peer },
    privateKey,
    256,
  )
  const hkdf = await subtle.importKey('raw', shared, 'HKDF', false, [
    'deriveKey',
  ])
  return subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: bufferSource(salt), info: INFO },
    hkdf,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export async function seal(
  plain: Uint8Array,
  recipientPublicKey: Uint8Array,
): Promise<Uint8Array> {
  const eph = await subtle.generateKey(X25519, true, ['deriveBits'])
  const ephPub = new Uint8Array(await subtle.exportKey('raw', eph.publicKey))
  // salt = ephPub || recipientPub binds the key to both parties.
  const key = await deriveAesKey(
    eph.privateKey,
    recipientPublicKey,
    concat(ephPub, recipientPublicKey),
  )
  const nonce = globalThis.crypto.getRandomValues(new Uint8Array(NONCE_BYTES))
  const ct = new Uint8Array(
    await subtle.encrypt(
      { name: 'AES-GCM', iv: nonce },
      key,
      bufferSource(plain),
    ),
  )
  return concat(ephPub, nonce, ct)
}

/**
 * A recipient whose private key WebCrypto has already imported — the form
 * `open` accepts when the key must never leave the WebCrypto store.
 *
 * Why this exists: the decrypt path needs the recipient's PUBLIC key, because
 * the HKDF salt is `ephPub || recipientPub`. Given only PKCS8 bytes, `open`
 * recovers that public half via `publicBytesFromPrivate`, whose JWK round trip
 * needs an **extractable** import — and that round trip is the only thing in
 * the whole decrypt path that does. The ECDH itself (`deriveBits`) works fine
 * on a key imported with `extractable: false`. So a holder of a
 * non-extractable key hands us the public half directly and skips the round
 * trip entirely.
 *
 * Misuse: we cannot check that `publicKey` is really the public half of `key`
 * — if we could derive it from a non-extractable key, this type would have no
 * reason to exist. A mismatch is nonetheless **never silent**: the public key
 * is an HKDF salt input, so a wrong one derives a different AES key and
 * AES-GCM rejects the tag. The failure is a `SealedBoxOpenError`,
 * indistinguishable from "wrong private key" — correct, but no more specific
 * than that.
 */
export interface ImportedRecipientKey {
  /**
   * X25519 private key with the `deriveBits` usage. May be — and for the
   * reason this type exists, normally is — non-extractable.
   */
  key: CryptoKey
  /** The raw 32-byte X25519 public key belonging to `key`. */
  publicKey: Uint8Array
}

/**
 * AES-GCM rejected the ciphertext. WebCrypto's own error for this is an
 * `OperationError` reading "The operation failed for an operation-specific
 * reason", which tells a caller nothing; this says what the possibilities are
 * and keeps the original as `cause`.
 *
 * It deliberately does not say WHICH of them it was — the three causes are
 * cryptographically indistinguishable here (every one of them means the
 * derived AES key is not the key the box was sealed under), and distinguishing
 * them would mean reasoning about key material in an error path, which is
 * exactly where key material must not go.
 */
export class SealedBoxOpenError extends Error {
  constructor(cause: unknown) {
    super(
      'sealed box did not open: wrong recipient private key, a public key ' +
        'that does not belong to it, or a corrupt box',
      { cause },
    )
    this.name = 'SealedBoxOpenError'
  }
}

/**
 * Opens a sealed box, in either of two forms:
 *
 * - `open(box, pkcs8Bytes)` — the original and still the one every caller in
 *   this repo uses: the recipient's raw PKCS8 private key bytes.
 * - `open(box, { key, publicKey })` — an already-imported private key, which
 *   may be non-extractable, plus its public half. See
 *   {@link ImportedRecipientKey} for why the public half must come along.
 *
 * Both forms derive the same AES key from the same bytes; the wire format is
 * identical either way. Throws {@link SealedBoxOpenError} if the box does not
 * authenticate.
 *
 * (One signature rather than two overloads: the two forms differ only in the
 * second parameter's type, which `@typescript-eslint/unified-signatures`
 * rightly flags as a union wearing a disguise.)
 */
export async function open(
  sealed: Uint8Array,
  recipient: Uint8Array | ImportedRecipientKey,
): Promise<Uint8Array> {
  if (sealed.length < PUB_BYTES + NONCE_BYTES + TAG_BYTES)
    throw new Error('sealed box too short')
  const ephPub = sealed.subarray(0, PUB_BYTES)
  const nonce = sealed.subarray(PUB_BYTES, PUB_BYTES + NONCE_BYTES)
  const ct = sealed.subarray(PUB_BYTES + NONCE_BYTES)
  const { privateKey, publicKey } = await resolveRecipient(recipient)
  // Unchanged wire format: salt is still `ephPub || recipientPub`, info is
  // still INFO. Both recipient forms land on exactly these bytes — that is
  // the point of splitting the resolution out rather than branching here.
  const key = await deriveAesKey(privateKey, ephPub, concat(ephPub, publicKey))
  try {
    return new Uint8Array(
      await subtle.decrypt(
        { name: 'AES-GCM', iv: bufferSource(nonce) },
        key,
        bufferSource(ct),
      ),
    )
  } catch (cause) {
    throw new SealedBoxOpenError(cause)
  }
}

/**
 * Normalises either recipient form to the pair the derivation needs: a
 * `deriveBits`-capable private key and the matching raw public bytes.
 *
 * The PKCS8 form imports the private key non-extractable (nothing here needs
 * to export it) and pays for the public half separately; the imported form is
 * already in the shape we want and is only validated.
 */
async function resolveRecipient(
  recipient: Uint8Array | ImportedRecipientKey,
): Promise<{ privateKey: CryptoKey; publicKey: Uint8Array }> {
  if (recipient instanceof Uint8Array) {
    return {
      privateKey: await subtle.importKey(
        'pkcs8',
        bufferSource(recipient),
        X25519,
        false,
        ['deriveBits'],
      ),
      publicKey: await publicBytesFromPrivate(recipient),
    }
  }
  // Cheap structural checks. They cannot catch a mismatched pair (see
  // ImportedRecipientKey), but they turn the three mistakes that ARE
  // detectable into a named error instead of a WebCrypto `OperationError` or,
  // worse, a `SealedBoxOpenError` that sends the caller hunting for the wrong
  // bug. Lengths and algorithm names are public metadata, never key material.
  const { key, publicKey } = recipient
  if (key.type !== 'private' || key.algorithm.name !== X25519.name)
    throw new Error(
      `recipient key must be an X25519 private key, got a ${key.algorithm.name} ${key.type} key`,
    )
  if (!key.usages.includes('deriveBits'))
    throw new Error("recipient key was imported without the 'deriveBits' usage")
  if (publicKey.length !== PUB_BYTES)
    throw new Error(
      `recipient public key must be ${String(PUB_BYTES)} bytes, got ${String(publicKey.length)}`,
    )
  return { privateKey: key, publicKey }
}

// WebCrypto has no "public from private" for X25519; import the PKCS8 as JWK,
// drop `d`, re-import as public, export raw. Works in Node 24, Workers, and
// browsers.
//
// The `extractable: true` import here is the ONE step of the decrypt path that
// requires an extractable key, which is why `open`'s ImportedRecipientKey form
// exists to route around it. Nothing else in this module needs it.
// Exported because a daemon holding the tenant PRIVATE key is the only
// thing on the host that can tell a local CLI which tenant PUBLIC key to
// seal a message to (`doctor`'s `tenantPubkey`). Deriving it beats
// shipping the public half around separately, where the two could drift
// apart and produce messages nobody can open.
export async function publicBytesFromPrivate(
  pkcs8: Uint8Array,
): Promise<Uint8Array> {
  const priv = await subtle.importKey(
    'pkcs8',
    bufferSource(pkcs8),
    X25519,
    true,
    ['deriveBits'],
  )
  const jwk = await subtle.exportKey('jwk', priv)
  // Dropping `d` (the private scalar) is the whole point of this destructure.
  const { d: _d, ...pubJwk } = jwk
  const pub = await subtle.importKey(
    'jwk',
    { ...pubJwk, key_ops: [] },
    X25519,
    true,
    [],
  )
  return new Uint8Array(await subtle.exportKey('raw', pub))
}
